import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { useInsert } from '../../hooks/useInsert';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { Skeleton } from '../ui/skeleton';
import { searchImages, apiErrorMessage } from '../../lib/projectApi';
import { Search, Image as ImageIcon, Upload, Loader2, X, Check, RefreshCw, ImageOff } from 'lucide-react';
import { toast } from 'sonner';

const SUGGESTIONS = ['abstract background', 'business team', 'nature landscape', 'food', 'city skyline', 'texture', 'technology', 'flowers'];
const SOURCES = [['all', 'All'], ['unsplash', 'Unsplash'], ['pexels', 'Pexels']];

const MODE_META = {
  insert: { title: 'Online Pictures', action: 'Insert Picture', description: 'Search free photos and insert one into your page.' },
  replace: { title: 'Change Picture', action: 'Replace Picture', description: 'Pick a new photo for the selected picture frame.' },
  background: { title: 'Picture Background', action: 'Use as Background', description: 'Choose a photo to use as the background of this page.' },
  fill: { title: 'Picture Fill', action: 'Fill with Picture', description: 'Fill the selected shape with a photo.' },
};

const SOURCE_LINK = { unsplash: 'https://unsplash.com/?utm_source=wannapublish&utm_medium=referral', pexels: 'https://www.pexels.com' };

/**
 * Online Pictures: Unsplash/Pexels search (through the backend, which owns the API keys)
 * plus local upload. Honors state.galleryInsertMode: insert | replace | background | fill.
 */
export const ImageGalleryModal = () => {
  const { state, dispatch, getSelectedObjects } = useEditor();
  const ins = useInsert();
  const mode = state.galleryInsertMode || 'insert';
  const meta = MODE_META[mode] || MODE_META.insert;

  const [query, setQuery] = useState('');
  const [source, setSource] = useState('all');
  const [page, setPage] = useState(1);
  const [images, setImages] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error | empty
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [uploaded, setUploaded] = useState(null);
  const [tab, setTab] = useState('search');
  const [loadingMore, setLoadingMore] = useState(false);
  const lastQuery = useRef('');
  const fileRef = useRef(null);

  const close = useCallback(() => {
    dispatch({ type: 'HIDE_IMAGE_GALLERY' });
    if (state.activeTool !== 'select') dispatch({ type: 'SET_ACTIVE_TOOL', payload: 'select' });
  }, [dispatch, state.activeTool]);

  const runSearch = useCallback(async (q, src, pg = 1, append = false) => {
    const term = (q || '').trim() || 'abstract background';
    lastQuery.current = term;
    if (append) setLoadingMore(true); else { setStatus('loading'); setError(''); }
    try {
      const results = await searchImages(term, src, pg);
      const list = append ? (prev) => [...prev, ...results] : results;
      setImages(list);
      setStatus(results.length || append ? 'ready' : 'empty');
      setPage(pg);
    } catch (e) {
      setError(await apiErrorMessage(e, 'Image search is unavailable right now.'));
      if (!append) { setImages([]); setStatus('error'); }
    } finally { setLoadingMore(false); }
  }, []);

  useEffect(() => { runSearch('', 'all', 1); }, [runSearch]);

  const submit = (e) => { e?.preventDefault(); setSelected(null); runSearch(query, source, 1); };
  const changeSource = (s) => { setSource(s); setSelected(null); runSearch(query || lastQuery.current, s, 1); };

  const onUpload = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file.'); return; }
    const r = new FileReader();
    r.onload = () => { setUploaded({ id: 'upload', url: r.result, description: file.name, photographer: '', source: 'upload', width: 0, height: 0 }); setSelected({ id: 'upload', url: r.result, description: file.name, source: 'upload' }); };
    r.readAsDataURL(file);
  };

  const apply = async () => {
    const img = selected;
    if (!img) return;
    const attribution = img.source && img.source !== 'upload' && img.source !== 'sample' ? { photographer: img.photographer, source: img.source, sourceUrl: SOURCE_LINK[img.source] } : null;
    const altText = img.description || '';
    if (mode === 'replace') {
      const targets = getSelectedObjects().filter((o) => o.type === 'image');
      if (!targets.length) { await ins.insertImageSrc(img.url, { altText, attribution }); }
      else dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: targets.map((o) => o.id), props: { src: img.url, placeholder: false, altText, attribution, crop: undefined } } });
      toast.success('Picture replaced');
    } else if (mode === 'background') {
      dispatch({ type: 'UPDATE_PAGE', payload: { index: state.currentPageIndex, data: { background: { type: 'image', image: { url: img.url, fit: 'cover' }, attribution } } } });
      toast.success('Page background updated');
    } else if (mode === 'fill') {
      const targets = getSelectedObjects().filter((o) => o.type === 'shape' || o.type === 'textbox' || o.type === 'text' || o.type === 'table');
      if (!targets.length) { await ins.insertImageSrc(img.url, { altText, attribution }); }
      else dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: targets.map((o) => o.id), props: { fill: { type: 'image', image: { url: img.url, fit: 'cover' }, attribution } } } });
      toast.success('Picture fill applied');
    } else {
      await ins.insertImageSrc(img.url, { altText, attribution });
      toast.success(attribution ? `Picture inserted \u2014 photo by ${attribution.photographer} on ${attribution.source === 'unsplash' ? 'Unsplash' : 'Pexels'}` : 'Picture inserted');
    }
    close();
  };

  return (
    <Dialog open={state.showImageGallery} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-4xl w-[calc(100vw-2rem)] h-[min(82vh,720px)] bg-background border-border p-0 flex flex-col gap-0 [&>button]:hidden" data-testid="image-gallery-modal">
        <DialogHeader className="px-4 py-3 border-b border-border shrink-0 text-left">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="text-base font-outfit text-foreground" data-testid="image-gallery-title">{meta.title}</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground truncate">{meta.description}</DialogDescription>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button onClick={apply} disabled={!selected} size="sm" data-testid="insert-image-btn"><Check className="w-4 h-4 mr-1.5" /> {meta.action}</Button>
              <Button variant="ghost" size="icon" onClick={close} className="w-8 h-8 text-muted-foreground hover:text-foreground" aria-label="Close" data-testid="close-gallery-btn"><X className="w-4 h-4" /></Button>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="w-full bg-muted/60 rounded-none border-b border-border h-10 p-1 shrink-0 justify-start">
            <TabsTrigger value="search" className="data-[state=active]:bg-background data-[state=active]:text-foreground text-muted-foreground px-4" data-testid="gallery-tab-search"><Search className="w-4 h-4 mr-2" /> Online Pictures</TabsTrigger>
            <TabsTrigger value="upload" className="data-[state=active]:bg-background data-[state=active]:text-foreground text-muted-foreground px-4" data-testid="gallery-tab-upload"><Upload className="w-4 h-4 mr-2" /> From Your Computer</TabsTrigger>
          </TabsList>

          <TabsContent value="search" className="flex-1 m-0 flex flex-col overflow-hidden">
            <form onSubmit={submit} className="px-4 pt-3 pb-2 border-b border-border shrink-0 space-y-2">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search free photos (e.g. mountains, coffee, team)" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 h-9 bg-background border-border text-foreground" autoFocus data-testid="image-search-input" />
                </div>
                <Button type="submit" size="sm" className="h-9" disabled={status === 'loading'} data-testid="image-search-button">{status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}</Button>
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <div className="flex items-center rounded-md border border-border overflow-hidden mr-2" role="radiogroup" aria-label="Photo source">
                  {SOURCES.map(([id, l]) => <button key={id} type="button" role="radio" aria-checked={source === id} onClick={() => changeSource(id)} className={`h-6 px-2 text-[11px] transition-colors ${source === id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`} data-testid={`image-source-${id}`}>{l}</button>)}
                </div>
                {SUGGESTIONS.map((s) => <button key={s} type="button" onClick={() => { setQuery(s); setSelected(null); runSearch(s, source, 1); }} className="h-6 px-2 rounded-full border border-border text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" data-testid={`image-suggestion-${s.replace(/\s+/g, '-')}`}>{s}</button>)}
              </div>
            </form>

            <ScrollArea className="flex-1">
              <div className="p-4">
                {status === 'loading' && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="image-results-loading">{Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} className="aspect-square rounded-lg" />)}</div>
                )}
                {status === 'error' && (
                  <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="image-results-error">
                    <ImageOff className="w-8 h-8 text-muted-foreground mb-2" />
                    <div className="text-sm font-medium text-foreground">Could not load pictures</div>
                    <div className="text-xs text-muted-foreground mt-1 max-w-sm">{error}</div>
                    <Button variant="outline" size="sm" className="mt-4" onClick={() => runSearch(query, source, 1)} data-testid="image-results-retry"><RefreshCw className="w-4 h-4 mr-2" /> Try again</Button>
                  </div>
                )}
                {status === 'empty' && (
                  <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="image-results-empty">
                    <Search className="w-8 h-8 text-muted-foreground mb-2" />
                    <div className="text-sm font-medium text-foreground">No pictures found for “{lastQuery.current}”</div>
                    <div className="text-xs text-muted-foreground mt-1">Try a different word or one of the suggestions above.</div>
                  </div>
                )}
                {status === 'ready' && (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="image-results-grid">
                      {images.map((img) => {
                        const on = selected?.id === img.id;
                        return (
                          <button
                            key={img.id}
                            type="button"
                            onClick={() => setSelected(img)}
                            onDoubleClick={() => { setSelected(img); setTimeout(apply, 0); }}
                            className={`relative aspect-square rounded-lg overflow-hidden group text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-[transform,box-shadow] duration-150 hover:scale-[1.015] ${on ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''}`}
                            aria-pressed={on}
                            data-testid={`gallery-image-${img.id}`}
                          >
                            <img src={img.thumb_url || img.url} alt={img.description || 'Photo'} loading="lazy" className="w-full h-full object-cover" />
                            <div className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 transition-opacity duration-150 ${on ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                              <p className="text-[11px] text-white truncate">{img.description || 'Untitled'}</p>
                              {img.photographer && <p className="text-[10px] text-white/80 truncate">by {img.photographer}{img.source === 'unsplash' ? ' · Unsplash' : img.source === 'pexels' ? ' · Pexels' : ''}</p>}
                            </div>
                            {on && <span className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center shadow"><Check className="w-4 h-4 text-primary-foreground" /></span>}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex justify-center mt-4">
                      <Button variant="outline" size="sm" onClick={() => runSearch(lastQuery.current, source, page + 1, true)} disabled={loadingMore} data-testid="image-results-load-more">{loadingMore ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Load more</Button>
                    </div>
                  </>
                )}
              </div>
            </ScrollArea>
            <div className="px-4 py-2 border-t border-border text-[11px] text-muted-foreground flex items-center justify-between gap-3 shrink-0">
              <span data-testid="image-attribution-note">Photos are provided by <a href={SOURCE_LINK.unsplash} target="_blank" rel="noreferrer" className="underline hover:text-foreground">Unsplash</a> and <a href={SOURCE_LINK.pexels} target="_blank" rel="noreferrer" className="underline hover:text-foreground">Pexels</a> under their free licenses. Attribution is stored with each picture.</span>
              {selected && selected.source !== 'upload' && <span className="truncate text-foreground" data-testid="image-selected-attribution">Selected: {selected.description || 'Photo'}{selected.photographer ? ` by ${selected.photographer}` : ''}</span>}
            </div>
          </TabsContent>

          <TabsContent value="upload" className="flex-1 m-0 overflow-auto">
            <div className="p-6 flex flex-col items-center gap-4">
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onUpload(e.dataTransfer.files?.[0]); }}
                className="w-full max-w-lg rounded-lg border-2 border-dashed border-border hover:border-primary/60 hover:bg-muted/40 p-8 text-center cursor-pointer transition-colors"
                data-testid="image-upload-drop-zone"
              >
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} data-testid="image-upload-input" />
                <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
                <div className="mt-2 text-sm font-medium text-foreground">Drop an image here or click to browse</div>
                <div className="text-xs text-muted-foreground mt-1">PNG, JPG, GIF, WebP or SVG</div>
              </div>
              {uploaded && (
                <div className="w-full max-w-lg rounded-lg border border-border bg-card p-3 flex items-center gap-3" data-testid="image-upload-preview">
                  <img src={uploaded.url} alt={uploaded.description} className="h-20 w-20 object-cover rounded-md border border-border bg-white" />
                  <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{uploaded.description}</div><div className="text-xs text-muted-foreground">Ready to {meta.action.toLowerCase()}</div></div>
                  <Button size="sm" onClick={apply} data-testid="image-upload-apply"><Check className="w-4 h-4 mr-1.5" /> {meta.action}</Button>
                </div>
              )}
              {!uploaded && <div className="text-xs text-muted-foreground flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Uploaded images are embedded in your publication.</div>}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
